import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  getStoredAdmin,
  hasAdminPermission,
  logoutAdmin,
  refreshAdminProfile,
} from "../../api/adminAuthApi";
import logo from "../../assets/images/logo.png";

import "./app-layout.scss";

const menuGroups = [
  {
    title: "Dashboard",
    items: [{ label: "Dashboard", icon: "ti-layout-dashboard", path: "/", end: true }],
  },
  {
    title: "Operations",
    items: [
      { label: "Orders", icon: "ti-receipt", path: "/orders", permission: "orders" },
    ],
  },
  {
    title: "Merchant",
    items: [
      { label: "Merchants", icon: "ti-building-store", path: "/merchants", permission: "merchants" },
    ],
  },
  {
    title: "Administration",
    items: [
      { label: "Administrators", icon: "ti-users", path: "/administrators", permission: "administrators" },
      { label: "Merchant Owners", icon: "ti-user-shield", path: "/merchant-owners", permission: "merchants" },
      { label: "Settings", icon: "ti-settings", path: "/settings", permission: "settings" },
      { label: "Audit Trail", icon: "ti-history", path: "/audit-trail", permission: "administrators" },
    ],
  },
  {
    title: "Reports",
    items: [{ label: "Reports", icon: "ti-chart-bar", path: "/reports", permission: "reports" }],
  },
];

const pageTitles = {
  "/": "Dashboard",
  "/orders": "Orders",
  "/merchants": "Merchants",
  "/administrators": "Administrators",
  "/merchant-owners": "Merchant Owners",
  "/settings": "Settings",
  "/audit-trail": "Audit Trail",
  "/reports": "Reports",
  "/profile": "Edit Profile",
};

const resolvePageTitle = (pathname) => {
  if (pathname.startsWith("/orders/")) {
    return "Order Detail";
  }

  if (pathname.startsWith("/merchant-owners/")) {
    return pathname.endsWith("/create") ? "Add Merchant Owner" : "Manage Merchant Owner";
  }

  return pageTitles[pathname] || "Admin Portal";
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [admin, setAdmin] = useState(() => getStoredAdmin());
  const adminName = admin?.name || "Admin";
  const adminInitial = adminName.charAt(0).toUpperCase();

  useEffect(() => {
    const syncAdmin = () => setAdmin(getStoredAdmin());

    window.addEventListener("admin-permissions-changed", syncAdmin);
    refreshAdminProfile().then((profile) => {
      if (profile) {
        setAdmin(profile);
      } else {
        syncAdmin();
      }
    });

    return () => window.removeEventListener("admin-permissions-changed", syncAdmin);
  }, []);

  const visibleMenuGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => hasAdminPermission(item.permission, admin)),
    }))
    .filter((group) => group.items.length > 0);

  const handleLogout = async () => {
    await logoutAdmin();
    navigate("/login");
  };

  return (
    <div className="app-layout">
      <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
        <div className="app-layout__brand">
          <img className="app-layout__brand-mark" src={logo} alt="" />
          <div>
            <div className="app-layout__brand-name">SecondBite</div>
            <div className="app-layout__brand-subtitle">Admin Portal</div>
          </div>
        </div>

        <nav className="app-layout__menu">
          {visibleMenuGroups.map((group) => (
            <div className="app-layout__menu-group" key={group.title}>
              <div className="app-layout__menu-title">{group.title}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="app-layout__menu-icon"><i className={`ti ${item.icon}`} aria-hidden="true"></i></span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="app-layout__mobile-logout">
          <button className="app-layout__logout" type="button" onClick={handleLogout}>
            <span className="app-layout__logout-icon"><i className="ti ti-logout" aria-hidden="true"></i></span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          className="app-layout__overlay"
          type="button"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="app-layout__main">
        <header className="app-layout__topbar">
          <button
            className="app-layout__menu-toggle"
            type="button"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="ti ti-menu-2" aria-hidden="true"></i>
          </button>

          <div>
            <p className="app-layout__eyebrow">MVP B2B Platform</p>
            <h1 className="app-layout__title">{resolvePageTitle(location.pathname)}</h1>
          </div>

          <div className="app-layout__profile-wrap">
            <button
              className={`app-layout__profile ${profileOpen ? "app-layout__profile--open" : ""}`}
              type="button"
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              onClick={() => setProfileOpen((open) => !open)}
            >
              <span className="app-layout__avatar">{adminInitial}</span>
              <span>{adminName}</span>
              <span className="app-layout__profile-chevron"><i className="ti ti-chevron-down" aria-hidden="true"></i></span>
            </button>

            {profileOpen && (
              <>
                <button
                  className="app-layout__profile-backdrop"
                  type="button"
                  aria-label="Close profile menu"
                  onClick={() => setProfileOpen(false)}
                />
                <div className="app-layout__profile-menu" role="menu">
                  <button
                    className="app-layout__profile-menu-item"
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      navigate("/profile");
                    }}
                  >
                    <span className="app-layout__profile-menu-icon"><i className="ti ti-user-circle" aria-hidden="true"></i></span>
                    <span>Edit Profile</span>
                  </button>

                  <button
                    className="app-layout__profile-menu-item app-layout__profile-menu-item--danger"
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <span className="app-layout__profile-menu-icon"><i className="ti ti-logout" aria-hidden="true"></i></span>
                    <span>Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <section className="app-layout__content">
          <Outlet />
        </section>
      </main>
    </div>
  );
};

export default AppLayout;
